import React, { useMemo, useState } from "react";
import { UseFormReturn } from "react-hook-form";
import { FormField, FormItem, FormMessage } from "@/components/ui/form";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Plus, Trash2, CalendarIcon } from "lucide-react";
import { SortableOption } from "@/types/fieldsOptions";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { TimePicker } from "@/components/admin/locations/components/time-picker";
import { cn } from "@/lib/utils";
import { format } from "date-fns";

interface RegistrationEventRowProps {
  // Use a generic form type to allow reuse in bulk modal
  form: UseFormReturn<any>;
  index: number;
  isFirst: boolean;
  isLast: boolean;
  registrationEventOptions: SortableOption[];
  defaultEventKey?: string;
  onAdd: () => void;
  onRemove: () => void;
  markFieldAsModified?: (fieldName: string) => void;
  disabled?: boolean;
}

export const RegistrationEventRow: React.FC<RegistrationEventRowProps> = ({
  form,
  index,
  isFirst,
  isLast,
  registrationEventOptions,
  defaultEventKey = "",
  onAdd,
  onRemove,
  markFieldAsModified = () => {},
  disabled = false
}) => {
  const [calendarOpen, setCalendarOpen] = useState(false);
  
  // Get current values to check for validation
  const regEvents = form.watch("regEvents") || [];
  const currentEventKey = form.watch(`regEvents.${index}.eventKey`);
  const currentDate = form.watch(`regEvents.${index}.when`);
  const wasTimeProvided = form.watch(`regEvents.${index}.wasTimeProvided`);

  // Check if current event key conflicts with unique constraints
  const hasConflict = useMemo(() => {
    const uniqueKeys = ["e_posted", "e_open", "e_closes"];
    if (!uniqueKeys.includes(currentEventKey)) return false;
    
    const count = regEvents.filter((event, idx) => 
      idx !== index && event.eventKey === currentEventKey
    ).length;
    
    return count > 0;
  }, [regEvents, currentEventKey, index]);

  const handleTimeChange = (minutes: number | null) => {
    if (disabled) return;
    if (currentDate && minutes !== null) {
      const newDate = new Date(currentDate);
      const hours = Math.floor(minutes / 60);
      const mins = minutes % 60;
      newDate.setHours(hours, mins, 0, 0);
      form.setValue(`regEvents.${index}.when`, newDate);
      form.setValue(`regEvents.${index}.wasTimeProvided`, true);
      markFieldAsModified("regEvents");
    } else if (minutes === null && currentDate) {
      // Reset to midnight if time is cleared
      const newDate = new Date(currentDate);
      newDate.setHours(0, 0, 0, 0);
      form.setValue(`regEvents.${index}.when`, newDate);
      form.setValue(`regEvents.${index}.wasTimeProvided`, false);
      markFieldAsModified("regEvents");
    }
  };

  const handleDateChange = (date: Date | undefined) => {
    if (disabled) return;
    if (date && wasTimeProvided && currentDate) {
      // Preserve the time if it was already set
      const hours = currentDate.getHours();
      const minutes = currentDate.getMinutes();
      date.setHours(hours, minutes, 0, 0);
    } else if (date) {
      // Set to midnight if no time was provided
      date.setHours(0, 0, 0, 0);
    }
    form.setValue(`regEvents.${index}.when`, date);
    if (!date) {
      form.setValue(`regEvents.${index}.wasTimeProvided`, false);
    }
    markFieldAsModified("regEvents");
    setCalendarOpen(false);
  };

  // Get time in minutes for TimePicker
  const timeInMinutes = currentDate ? (currentDate.getHours() * 60 + currentDate.getMinutes()) : null;

  return (
    <div className="flex gap-2 items-start">
      {/* Event Type Dropdown */}
      <FormField
        control={form.control}
        name={`regEvents.${index}.eventKey`}
        render={({ field }) => (
          <FormItem className="flex-1 min-w-[200px]">
            <Select
              value={field.value || defaultEventKey}
              onValueChange={(value) => {
                if (disabled) return;
                field.onChange(value);
                markFieldAsModified("regEvents");
              }}
              disabled={disabled}
            >
              <SelectTrigger className={hasConflict ? "border-red-500" : ""}>
                <SelectValue placeholder="Select event type" />
              </SelectTrigger>
              <SelectContent>
                {registrationEventOptions.map((option) => (
                  <SelectItem key={option.id} value={option.id}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {hasConflict && (
              <p className="text-xs text-red-500 mt-1">
                This event type already exists
              </p>
            )}
            <FormMessage />
          </FormItem>
        )}
      />

      {/* Date Input */}
      <FormField
        control={form.control}
        name={`regEvents.${index}.when`}
        render={({ field }) => (
          <FormItem className="flex-shrink-0">
            <Popover
              open={disabled ? false : calendarOpen}
              onOpenChange={(open) => {
                if (disabled) return;
                setCalendarOpen(open);
              }}
            >
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  disabled={disabled}
                  className={cn(
                    "w-[140px] justify-start text-left font-normal",
                    !field.value && "text-muted-foreground"
                  )}
                >
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {field.value ? format(field.value, "MMM d, yyyy") : "Select date"}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="single"
                  selected={field.value}
                  onSelect={handleDateChange}
                  initialFocus
                />
              </PopoverContent>
            </Popover>
            <FormMessage />
          </FormItem>
        )}
      />

      {/* Time Input */}
      <div className="flex-shrink-0 w-[140px]">
        <TimePicker
          value={wasTimeProvided ? timeInMinutes : null}
          onChange={handleTimeChange}
          disabled={disabled || !currentDate}
          placeholder="Time (optional)"
        />
      </div>

      {/* Note Input */}
      <FormField
        control={form.control}
        name={`regEvents.${index}.note`}
        render={({ field }) => (
          <FormItem className="flex-1">
            <Input
              {...field}
              placeholder="Optional note"
              disabled={disabled}
              onChange={(e) => {
                if (disabled) return;
                field.onChange(e.target.value);
                markFieldAsModified("regEvents");
              }}
            />
            <FormMessage />
          </FormItem>
        )}
      />

      {/* Action Buttons */}
      <div className="flex gap-1 flex-shrink-0">
        {!isLast && (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={() => !disabled && onAdd()}
            disabled={disabled}
            className="h-9 w-9"
          >
            <Plus className="h-4 w-4" />
          </Button>
        )}
        
        {!isFirst && (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={() => !disabled && onRemove()}
            disabled={disabled}
            className="h-9 w-9"
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        )}
      </div>
    </div>
  );
}; 